import React, {
	type MouseEvent,
	type KeyboardEvent,
	useCallback,
	useState,
	useEffect,
} from 'react';
import { styled } from '@compiled/react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import Button from '@atlaskit/button';
import BranchesIcon from '@atlaskit/icon/core/migration/branch--bitbucket-branches';
import BoardIcon from '@atlaskit/icon/core/migration/board';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import DashboardIcon from '@atlaskit/icon/core/migration/dashboard';
import PeopleIcon from '@atlaskit/icon/core/migration/people-group--people';
import RoadmapIcon from '@atlaskit/icon/core/migration/roadmap';
import SettingsIcon from '@atlaskit/icon/core/migration/settings';
import ShipIcon from '@atlaskit/icon/core/migration/release--ship';

import Lozenge from '@atlaskit/lozenge';
import { SpotlightTarget as SpotlightTargetEP } from '@atlaskit/onboarding';
import {
	NavigationHeader,
	NavigationFooter,
	Section,
	SideNavigation,
	NestableNavigationContent,
} from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { PlanSidebarNavInfo } from '@atlassian/jira-navigation-apps-resources/src/services/sidebar/plan-sidebar-navigation/types.tsx';
import { ExpandableSection } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/expandable-section/index.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import {
	NavigationExpandableContentWrapper,
	NavigationContentWrapper,
	NavigationHeaderWrapper,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/navigation-component-wrappers/index.tsx';
import { ScreenReaderMenuHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/screen-reader-header/index.tsx';
import { usePlan } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/plan/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { NestingMenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import { ProjectFooter } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/project-footer/main.tsx';
import { CtpSpotlights } from '@atlassian/jira-portfolio-3-onboarding/src/controllers/ctp-spotlights/index.tsx';
import { CttSpotlights } from '@atlassian/jira-portfolio-3-onboarding/src/controllers/ctt-spotlights/index.tsx';
import { Spotlights } from '@atlassian/jira-portfolio-3-onboarding/src/controllers/spotlights/index.tsx';
import { SpotlightTarget } from '@atlassian/jira-portfolio-3-onboarding/src/ui/spotlight-target/index.tsx';
import { useAdvancedRoadmapsSummaryDataResource } from '@atlassian/jira-router-resources-advanced-roadmaps-summary/src/index.tsx';
import { useRouter, generatePath } from '@atlassian/react-resource-router';
import { MENU_ID, PLANS_BASE_PATH, SECTION_ID, PAGES } from '../../common/constants.tsx';
import { PlanMenuHeader } from './header/index.tsx';
import messages from './messages.tsx';
import PlanningContainer from './planning-container/index.tsx';
import {
	useNestedAppOpen,
	useStack,
	isCrossTeamPlanningTemplateExperimentEnabled,
} from './utils.tsx';

export const Menu = () => {
	const [stack, setStack] = useStack();
	const [nestedAppOpen, setNestedAppOpen] = useNestedAppOpen();
	const { data, update: updateResource } = usePlan();
	const { formatMessage } = useIntl();
	const [
		{
			match: { params },
		},
	] = useRouter();

	const { planId, scenarioId } = params;
	const { clear: clearSummaryPageCache } = useAdvancedRoadmapsSummaryDataResource();
	const isCrossTeamPlanningTemplateEnabled = isCrossTeamPlanningTemplateExperimentEnabled();

	const generateHref = useCallback(
		(nextPage: string) => {
			const basePath = generatePath(PLANS_BASE_PATH, params);
			return nextPage ? `${basePath}/${nextPage}` : basePath;
		},
		[params],
	);

	const handleSettingsMenuItemClick = useCallback(
		(e: MouseEvent | KeyboardEvent) => {
			clearSummaryPageCache();
			if (!e.metaKey && !e.ctrlKey) {
				setNestedAppOpen(true);
			}
		},
		[setNestedAppOpen, clearSummaryPageCache],
	);

	const onPlanStatusUpdated = useCallback(() => {
		if (data) {
			const newStatus = data.planStatus === 'ARCHIVED' ? 'ACTIVE' : 'ARCHIVED';
			updateResource((oldState: PlanSidebarNavInfo | null) => ({
				...(oldState || data),
				planStatus: newStatus,
			}));
		}
	}, [updateResource, data]);

	const isSettingsPage = stack.includes(SECTION_ID.SETTINGS);
	const [submenuActive, setSubmenuActive] = useState(isSettingsPage);

	// setting submenu active in an effect to prevent UI changes while transitioning.
	useEffect(() => {
		setSubmenuActive(isSettingsPage);
	}, [isSettingsPage]);

	return (
		<ContextualAnalyticsData menuId={MENU_ID}>
			<SideNavigation label="">
				<NavigationHeaderWrapper>
					<NavigationHeader>
						<PlanMenuHeader
							planId={planId}
							scenarioId={scenarioId}
							planTitle={data?.title ?? ''}
							isReadOnly={data?.isReadOnly}
							isArchived={data?.planStatus === 'ARCHIVED'}
							onPlanStatusUpdated={onPlanStatusUpdated}
						/>
					</NavigationHeader>
				</NavigationHeaderWrapper>
				<NestableNavigationContent stack={stack} onChange={setStack} isDefaultFocusControl={false}>
					<ScreenReaderMenuHeader />
					<SpotlightTarget delayOn={isSettingsPage} name={Spotlights.Views}>
						<ChangeboardingAnchor>
							<Section>
								<SpotlightTargetEP name="summary-page-nav-item-spotlight">
									<MenuItem
										analytics={{ itemId: PAGES.summary }}
										href={generateHref(PAGES.summary)}
										iconBefore={<DashboardIcon spacing="spacious" label="" />}
										selectedOn={['software-plan-summary']}
									>
										{formatMessage(messages.summary)}
									</MenuItem>
								</SpotlightTargetEP>
								<EngagementSpotlight engagementId="summary-page-nav-item-spotlight" />
							</Section>
							<Section hasSeparator>
								<PlanningContainer submenuActive={submenuActive}>
									{isCrossTeamPlanningTemplateEnabled ? (
										<SpotlightTarget delayOn={isSettingsPage} name={CtpSpotlights.Timeline}>
											<MenuItem
												analytics={{ itemId: PAGES.timeline }}
												href={generateHref(PAGES.timeline)}
												iconBefore={<RoadmapIcon spacing="spacious" label="" />}
												selectedOn={['software-plan-timeline']}
											>
												{formatMessage(messages.timeline)}
											</MenuItem>
										</SpotlightTarget>
									) : (
										<MenuItem
											analytics={{ itemId: PAGES.timeline }}
											href={generateHref(PAGES.timeline)}
											iconBefore={<RoadmapIcon spacing="spacious" label="" />}
											selectedOn={['software-plan-timeline']}
										>
											{formatMessage(messages.timeline)}
										</MenuItem>
									)}
									<MenuItem
										analytics={{ itemId: PAGES.calendar }}
										href={generateHref(PAGES.calendar)}
										iconBefore={<CalendarIcon spacing="spacious" label="" />}
										selectedOn={['software-plan-calendar']}
									>
										{formatMessage(messages.calendar)}
									</MenuItem>
									<MenuItem
										analytics={{ itemId: PAGES.increments }}
										href={generateHref(PAGES.increments)}
										iconBefore={
											<BoardIcon spacing="spacious" label={formatMessage(messages.increments)} />
										}
										selectedOn={['software-plan-increments', 'software-plan-increment']}
									>
										{formatMessage(messages.increments)}{' '}
										<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
									</MenuItem>
									{isCrossTeamPlanningTemplateEnabled ? (
										<SpotlightTarget delayOn={isSettingsPage} name={CtpSpotlights.Teams}>
											<MenuItem
												analytics={{ itemId: PAGES.teams }}
												href={generateHref(PAGES.teams)}
												iconBefore={<PeopleIcon spacing="spacious" label="" />}
												selectedOn={['software-plan-teams']}
											>
												{formatMessage(messages.teams)}
											</MenuItem>
										</SpotlightTarget>
									) : (
										<MenuItem
											analytics={{ itemId: PAGES.teams }}
											href={generateHref(PAGES.teams)}
											iconBefore={<PeopleIcon spacing="spacious" label="" />}
											selectedOn={['software-plan-teams']}
										>
											{formatMessage(messages.teams)}
										</MenuItem>
									)}
									{data?.isReleaseEnabled === false &&
									fg('simple-plans-m2-release-feature-toggle') ? null : (
										<MenuItem
											analytics={{ itemId: PAGES.releases }}
											href={generateHref(PAGES.releases)}
											iconBefore={<ShipIcon spacing="spacious" label="" />}
											selectedOn={['software-plan-releases']}
										>
											{formatMessage(messages.releases)}
										</MenuItem>
									)}
									<MenuItem
										analytics={{ itemId: PAGES.dependenciesReport }}
										href={generateHref(PAGES.dependenciesReport)}
										iconBefore={
											<DependencyReportIcon>
												<BranchesIcon spacing="spacious" label="" />
											</DependencyReportIcon>
										}
										selectedOn={['software-plan-dependencies']}
									>
										{formatMessage(messages.dependencies)}
									</MenuItem>
								</PlanningContainer>
							</Section>
						</ChangeboardingAnchor>
					</SpotlightTarget>
					<NavigationContentWrapper submenuActive={submenuActive}>
						<Section hasSeparator>
							<SpotlightTarget delayOn={isSettingsPage} name={CttSpotlights.Settings}>
								<NestingMenuItem
									analytics={{ itemId: 'settings' }}
									href={generateHref('settings/scheduling')}
									iconBefore={<SettingsIcon spacing="spacious" label="" />}
									id={SECTION_ID.SETTINGS}
									title={formatMessage(messages.planSettings)}
									onClick={handleSettingsMenuItemClick}
									overrides={{
										BackMenuItem: {
											href: generateHref('timeline'),
											title: formatMessage(messages.backToPlan),
											wrapper: ({ children }) => (
												<SpotlightTarget delayOn name={CttSpotlights.BackToPlan}>
													{children}
												</SpotlightTarget>
											),
										},
									}}
								>
									<NavigationExpandableContentWrapper
										nestedAppOpen={nestedAppOpen}
										submenuActive={submenuActive}
									>
										<ExpandableSection
											title={formatMessage(messages.general)}
											headingLevel={3}
											submenuActive={submenuActive}
										>
											<MenuItem
												analytics={{ itemId: 'settings/plan-info' }}
												href={generateHref('settings/plan-info')}
												selectedOn={['plan-settings-plan-info']}
											>
												{formatMessage(messages.planInfo)}
											</MenuItem>
											<MenuItem
												analytics={{ itemId: 'settings/scheduling' }}
												href={generateHref('settings/scheduling')}
												selectedOn={['plan-settings-scheduling']}
											>
												{formatMessage(messages.scheduling)}
											</MenuItem>
											<MenuItem
												analytics={{ itemId: 'settings/saved-views' }}
												href={generateHref('settings/saved-views')}
												selectedOn={['plan-settings-saved-views']}
											>
												{formatMessage(messages.savedViews)}
											</MenuItem>
											<MenuItem
												analytics={{
													itemId: 'settings/program-boards',
												}}
												href={generateHref('settings/program-boards')}
												selectedOn={['plan-settings-program-boards']}
											>
												{formatMessage(messages.programBoards)}{' '}
												<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
											</MenuItem>
										</ExpandableSection>
									</NavigationExpandableContentWrapper>
									{/* Display option if the plan is NOT read-only */}
									{!data?.isReadOnly && (
										<>
											<NavigationExpandableContentWrapper
												nestedAppOpen={nestedAppOpen}
												submenuActive={submenuActive}
											>
												<ExpandableSection
													headingLevel={3}
													title={formatMessage(messages.sourceData)}
													submenuActive={submenuActive}
												>
													<SpotlightTarget
														delayOn={isSettingsPage}
														name={CttSpotlights.IssueSources}
													>
														<MenuItem
															analytics={{
																itemId: 'settings/issue-sources',
															}}
															href={generateHref('settings/issue-sources')}
															selectedOn={['plan-settings-issue-sources']}
														>
															{formatMessage(
																fg('jira-issue-terminology-refresh-m3')
																	? messages.issueSourcesIssueTermRefresh
																	: messages.issueSources,
															)}
														</MenuItem>
													</SpotlightTarget>
													<MenuItem
														analytics={{
															itemId: 'settings/exclusion-rules',
														}}
														href={generateHref('settings/exclusion-rules')}
														selectedOn={['plan-settings-exclusion-rules']}
													>
														{formatMessage(messages.exclusionRules)}
													</MenuItem>
													<MenuItem
														analytics={{
															itemId: 'settings/removed-issues',
														}}
														href={generateHref('settings/removed-issues')}
														selectedOn={['plan-settings-removed-issues']}
													>
														{formatMessage(
															fg('jira-issue-terminology-refresh-m3')
																? messages.removedIssuesIssueTermRefresh
																: messages.removedIssues,
														)}
													</MenuItem>
													<MenuItem
														analytics={{
															itemId: 'settings/find-your-issue',
														}}
														href={generateHref('settings/find-your-issue')}
														selectedOn={['plan-settings-find-your-issue']}
													>
														{formatMessage(
															fg('jira-issue-terminology-refresh-m3')
																? messages.findYourIssueIssueTermRefresh
																: messages.findYourIssue,
														)}
													</MenuItem>
													<MenuItem
														analytics={{
															itemId: 'settings/custom-fields',
														}}
														href={generateHref('settings/custom-fields')}
														selectedOn={['plan-settings-custom-fields']}
													>
														{formatMessage(messages.customFields)}
													</MenuItem>
												</ExpandableSection>
											</NavigationExpandableContentWrapper>

											<NavigationExpandableContentWrapper
												nestedAppOpen={nestedAppOpen}
												submenuActive={submenuActive}
											>
												<ExpandableSection
													headingLevel={3}
													title={formatMessage(messages.accessControl)}
													submenuActive={submenuActive}
												>
													<MenuItem
														analytics={{
															itemId: 'settings/permissions',
														}}
														href={generateHref('settings/permissions')}
														selectedOn={['plan-settings-permissions']}
													>
														{formatMessage(messages.permissions)}
													</MenuItem>
												</ExpandableSection>
											</NavigationExpandableContentWrapper>
										</>
									)}
									<NavigationExpandableContentWrapper
										nestedAppOpen={nestedAppOpen}
										submenuActive={submenuActive}
									>
										<ExpandableSection
											title={formatMessage(messages.advanced)}
											headingLevel={3}
											submenuActive={submenuActive}
										>
											{expVal('m1_features_page_experiment', 'cohort', 'not-enrolled') ===
												'variation' && (
												<MenuItem
													analytics={{ itemId: 'settings/features' }}
													href={generateHref('settings/features')}
													selectedOn={['plan-settings-features']}
												>
													{formatMessage(messages.features)}
												</MenuItem>
											)}
											<MenuItem
												analytics={{ itemId: 'settings/scenarios' }}
												href={generateHref('settings/scenarios')}
												selectedOn={['plan-settings-scenarios']}
											>
												{formatMessage(messages.scenarios)}
											</MenuItem>
											<MenuItem
												analytics={{
													itemId: 'settings/auto-scheduler',
												}}
												href={generateHref('settings/auto-scheduler')}
												selectedOn={['plan-settings-auto-scheduler']}
											>
												{formatMessage(messages.autoScheduler)}
											</MenuItem>
										</ExpandableSection>
									</NavigationExpandableContentWrapper>
								</NestingMenuItem>
							</SpotlightTarget>
						</Section>
					</NavigationContentWrapper>
				</NestableNavigationContent>
				<NavigationFooter>
					<ProjectFooter
						description={
							<Button
								spacing="none"
								appearance="subtle-link"
								href="https://support.atlassian.com/jira-software-cloud/docs/get-started-with-advanced-roadmaps/"
								target="_blank"
							>
								{formatMessage(messages.footerWhatArePlans)}
							</Button>
						}
					>
						{' '}
					</ProjectFooter>
				</NavigationFooter>
			</SideNavigation>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DependencyReportIcon = styled.div({
	transform: 'rotate(90deg)',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ChangeboardingAnchor = styled.div({
	marginLeft: token('space.negative.075'),
	marginRight: token('space.negative.075'),
	paddingLeft: token('space.075'),
	paddingRight: token('space.075'),
});
